import { inject, injectable } from '../../../../modules/common/di';
import { AdminApiDal } from '../adminApiDal';
import { ResponseType } from 'axios';
import { ApiResponseBase } from '../../../models/apiResponseBase';
import { GetOrganizationBillsApiResponse } from './getOrganizationBillsApiResponse';
import { GenerateOrganizationBillApiResponse } from './generateOrganizationBillApiResponse';
import { GetBillingItemsApiResponse } from './getBillingItemsApiResponse';
import { GetBillingPlansApiResponse } from './getBillingPlansApiResponse';
import { UpdateBillingPlanApiResponse, UpdateBillingPlanModel } from './updateBillingPlanApiResponse';
import { GetBillingPlanVersionsApiResponse } from './getBillingPlanVersionsApiResponse';
import { BillingPlanVersionModel, UpdateBillingPlanVersionApiResponse } from './updateBillingPlanVersionApiResponse';
import { GetOrganizationBillingPlansApiResponse } from './getOrganizationBillingPlansApiResponse';

/**
 * Billing API.
 * See {@link https://iotadmins.docs.apiary.io/#reference/billing}
 *
 * Billing APIs allow to configure billing plans for organizations
 * and regenerate existing bills created in the past.
 *
 * Billing APIs require an admin API key and that the user
 * has the organization billing permission 11.
 */
@injectable('BillingApi')
export class BillingApi {
  @inject('AdminApiDal') protected readonly dal!: AdminApiDal;

  // #region Billing Plans

  /**
   * Billing items are base services for which organizations can be changed.
   * An organization billing plan includes prices for each required billing item.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/get-billing-items}
   *
   * @returns {Promise<GetBillingItemsApiResponse>}
   */
  getBillingItems(): Promise<GetBillingItemsApiResponse> {
    return this.dal.get(`billingItems`);
  }

  /**
   * A billing plan is a collection of billing prices for each time period.
   * A billing plan must be assigned to an organization to generate bills.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/billing-plans/get-billing-plans}
   *
   * @returns {Promise<GetBillingPlansApiResponse>}
   */
  getBillingPlans(): Promise<GetBillingPlansApiResponse> {
    return this.dal.get(`billingPlans`);
  }

  /**
   * Create or update billing plan.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/billing-plans/create-or-update-billing-plan}
   *
   * @param {UpdateBillingPlanModel} billingPlan Billing plan data.
   * @param [params] Requested parameters.
   * @param {number} [params.planId] Biling plan ID to update.
   * @returns {Promise<UpdateBillingPlanApiResponse>}
   */
  setBillingPlan(billingPlan: UpdateBillingPlanModel,
    params?: {
      planId?: number,
    }): Promise<UpdateBillingPlanApiResponse> {
    return this.dal.post(`billingPlans`, billingPlan, {params: params});
  }

  /**
   * Billing plan version contains prices for billing items inside a billing plan for specific time period.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/billing-plans/get-billing-plan-versions}
   *
   * @param {number} planId Billing plan ID.
   * @returns {Promise<GetBillingPlanVersionsApiResponse>}
   */
  getBillingPlanVersions(planId: number): Promise<GetBillingPlanVersionsApiResponse> {
    return this.dal.get(`billingPlans/${encodeURIComponent(planId.toString())}/versions`);
  }

  /**
   * Create or update billing plan version.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/billing-plan-versions/create-or-update-billing-plan-version}
   *
   * @param {number} planId Billing plan ID.
   * @param {BillingPlanVersionModel} billingPlanVersion Biling plan version data.
   * @param [params] Requested parameters.
   * @param {number} [params.planVersionId] Billing plan version ID to update.
   * @returns {Promise<UpdateBillingPlanVersionApiResponse>}
   */
  setBillingPlanVersion(planId: number, billingPlanVersion: BillingPlanVersionModel,
    params?: {
      planVersionId?: number,
    }): Promise<UpdateBillingPlanVersionApiResponse> {
    return this.dal.post(`billingPlans/${encodeURIComponent(planId.toString())}/versions`, billingPlanVersion, {params: params});
  }

  // #endregion

  // #region Organization Billing Plans

  /**
   * Get organization billing plans.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/organization-billing-plans/get-organization-billing-plans}
   *
   * @param {number} organizationId Organization ID.
   * @returns {Promise<GetOrganizationBillingPlansApiResponse>}
   */
  getOrganizationBillingPlans(organizationId: number,
    params?: {
      startDate?: string|number,
      endDate?: string|number,
    }): Promise<GetOrganizationBillingPlansApiResponse> {
    return this.dal.get(`organizations/${encodeURIComponent(organizationId.toString())}/billingPlans`, {params: params});
  }

  /**
   * Set new organization billing plan.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/organization-billing-plans/set-organization-billing-plan}
   *
   * @param {number} organizationId Organization ID.
   * @param params Requested parameters.
   * @param {number} params.planId New  billing plan ID.
   * @param {string|number} [params.startDate] New billing plan start date.
   * @returns {Promise<ApiResponseBase>}
   */
  setOrganizationBillingPlan(organizationId: number,
    params: {
      planId: number,
      startDate?: string|number,
    }): Promise<ApiResponseBase> {
    return this.dal.post(`organizations/${encodeURIComponent(organizationId.toString())}/billingPlans`, void 0, {params: params});
  }

  /**
   * Delete organization billing plan.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/organization-billing-plans/delete-organization-billing-plan}
   *
   * @param {number} organizationId Organization ID.
   * @param [params] Requested parameters.
   * @param {string|number} [params.startDate] Stop billing date.
   * @returns {Prmomise<ApiResponseBase>}
   */
  deleteOrganizationBillingPlan(organizationId: number,
    params?: {
      startDate?: string|number,
    }): Promise<ApiResponseBase> {
    return this.dal.delete(`organizations/${encodeURIComponent(organizationId.toString())}/billingPlans`, {params: params});
  }

  // #endregion

  // #region Organization Bills

  /**
   * Get list of bills generated for the organization.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/organization-bills/get-bills}
   *
   * @param {number} organizationId Organization ID.
   * @param params Request parameters.
   * @param {string|number} params.startDate Start date.
   * @param {string|number} [params.endDate] End date, default is 1st day of the current month.
   * @returns {Promise<GetOrganizationBillsApiResponse>}
   */
  getOrganizationBills(organizationId: number,
    params: {
      startDate: string|number,
      endDate?: string|number,
    },
  ): Promise<GetOrganizationBillsApiResponse> {
    return this.dal.get(`organizations/${encodeURIComponent(organizationId.toString())}/bills`, {params: params});
  }

  /**
   * Generate a bill for one billing period.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/organization-bills/generate-bill}
   *
   * @param {number} organizationId Organization ID.
   * @param [params] Request parameters.
   * @param {string|number} [params.date] The date following the end of the target billing period. By default, the current date is used.
   * @returns {Promise<GenerateOrganizationBillApiResponse>}
   */
  generateOranizationBill(organizationId: number,
    params?: {
      date?: number|string;
    }
  ): Promise<GenerateOrganizationBillApiResponse> {
    return this.dal.put(`organizations/${encodeURIComponent(organizationId.toString())}/bills`, void 0, {params: params});
  }

  /**
   * Return an organization bill in PDF or CSV format.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/organization-bills/get-bill-content}
   *
   * @param {number} organizationId Organization ID.
   * @param {number} billId Bill ID.
   * @param params Request parameters.
   * @param {string} params.format Bill file format: pdf or csv.
   * @returns {Promise<ArrayBuffer|string|Object>}
   */
  getBillContent(organizationId: number, billId: number,
    params: {
      format: string
    }
  ): Promise<ArrayBuffer|string|object> {
    const contentType: ResponseType = 'blob';
    return this.dal.get(`organizations/${encodeURIComponent(organizationId.toString())}/bills/${encodeURIComponent(billId.toString())}`, {
      params: params,
      responseType: contentType,
    });
  }

  /**
   * The API removes previously assigned specific billing bot,
   * so that a default billing bot will be used for the organization.
   * See {@link https://iotadmins.docs.apiary.io/#reference/billing/billing-bot/remove-billing-bot}
   *
   * @param {number} organizationId Organization ID.
   * @returns {Promise<ApiResponseBase>}
   */
  removeBillingBot(organizationId: number): Promise<ApiResponseBase> {
    return this.dal.delete(`organizations/${encodeURIComponent(organizationId.toString())}/billingBot`);
  }

  // #endregion

}
